import React from 'react';

import { observer } from 'mobx-react';
import { useFormik } from 'formik';

import { Button, DangerButton, SecondaryButton } from 'components/Button';
import ControlPicker from 'components/ControlPicker';
import CustomModal from 'components/CustomModal';
import { DetailsRowContainer, RequiredMark } from 'components/Entities/AddEntityModal/styles';

import { InputHeader } from 'containers/Audits/components/StartAuditModal/styles';

import API from 'utils/api';
import { RiskControlOption } from 'utils/api/modules/entities/entities.types';
import { useStore } from 'utils/hooks/store';

import ControlSummary from './ControlSummary';

type AddRiskControlModalProps = {
  riskUuid: string;
  onClose: () => void;
  onAdded: () => void;
};

const AddRiskControlModal: React.FunctionComponent<AddRiskControlModalProps> = ({ riskUuid, onClose, onAdded }) => {
  const { riskDetails, controlDetails } = useStore();
  const pickerId = React.useId();
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);

  const request = React.useMemo(() => API.entities.searchRiskControlOptions(riskUuid), [riskUuid]);

  const formik = useFormik<{ control: RiskControlOption | null }>({
    initialValues: { control: null },
    onSubmit: async ({ control }, helpers) => {
      if (!control) {
        return;
      }
      const { added, refusal } = await riskDetails.addControl(riskUuid, control.uuid);
      if (added) {
        onAdded();
      } else if (refusal) {
        helpers.setFieldError('control', refusal);
      }
    },
  });

  const { values, errors, isSubmitting, dirty, setFieldValue, handleSubmit } = formik;

  const requestClose = (): void => {
    if (dirty) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <CustomModal
        openModal
        onClose={requestClose}
        modalTitle="Add control"
        cancelButton={
          <Button variant="outlined" size="large" onClick={requestClose}>
            Cancel
          </Button>
        }
        submitButton={
          <Button
            variant="contained"
            size="large"
            data-testid="add-risk-control-submit"
            disabled={!values.control || isSubmitting}
            onClick={() => handleSubmit()}
          >
            Add control
          </Button>
        }
      >
        <div data-testid="add-risk-control-modal">
          <DetailsRowContainer>
            <InputHeader as="label" htmlFor={pickerId}>
              Control <RequiredMark>*</RequiredMark>
            </InputHeader>
            <ControlPicker
              id={pickerId}
              request={request}
              value={values.control}
              onChange={(control) => setFieldValue('control', control)}
              refreshKey={controlDetails.libraryVersion}
              errorMessage={errors.control as string | undefined}
            />
          </DetailsRowContainer>
          {values.control && <ControlSummary control={values.control} />}
        </div>
      </CustomModal>
      <CustomModal
        openModal={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        modalTitle="Discard changes?"
        modalWidth="sm"
        cancelButton={<SecondaryButton onClick={() => setConfirmDiscard(false)}>Keep editing</SecondaryButton>}
        submitButton={
          <DangerButton onClick={onClose} data-testid="add-risk-control-discard">
            Discard
          </DangerButton>
        }
      >
        <p>The changes you made will not be saved.</p>
      </CustomModal>
    </>
  );
};

export default observer(AddRiskControlModal);
